/**
 * @fileoverview Toast Context Provider
 *
 * Provides toast notification functions to the component tree via React Context.
 * Uses the unified toast API for consistent notifications across the application.
 *
 * @module contexts/toastContext
 * @see {@link lib/toast-unified} for the underlying toast implementation
 * @see {@link hooks/useToast} for the consumer hook
 */

import React, { createContext, ReactNode, useMemo } from 'react';

import { toast, ToastOptions, PromiseMessages } from 'lib/toast-unified';

// =============================================================================
// Types
// =============================================================================

/** Options for toasts that commonly include a description (errors, warnings) */
export interface ToastWithDescriptionOptions extends ToastOptions {
  /** Detailed description shown below the main message */
  description?: string;
}

/** Action configuration for persistent toasts */
export interface ToastAction {
  /** Button label text */
  label: string;
  /** Click handler for the action button */
  onClick: () => void;
}

export interface ToastContextValue {
  // -------------------------------------------------------------------------
  // Simple notifications (backward compatible)
  // -------------------------------------------------------------------------

  /**
   * Display a success notification.
   * @param message - Success message to display
   * @param options - Optional configuration (duration, description, etc.)
   * @returns Toast ID for dismissal
   */
  toastSuccess: (message: string, options?: ToastOptions) => string | number;

  /**
   * Display an error notification with extended duration.
   * @param message - Error message to display
   * @param options - Optional configuration (description recommended for errors)
   * @returns Toast ID for dismissal
   */
  toastError: (message: string, options?: ToastWithDescriptionOptions) => string | number;

  /**
   * Display a warning notification.
   * @param message - Warning message to display
   * @param options - Optional configuration (description, duration, etc.)
   * @returns Toast ID for dismissal
   */
  toastWarning: (message: string, options?: ToastWithDescriptionOptions) => string | number;

  /**
   * Display an info notification.
   * @param message - Info message to display
   * @param options - Optional configuration (duration, etc.)
   * @returns Toast ID for dismissal
   */
  toastInfo: (message: string, options?: ToastOptions) => string | number;

  // -------------------------------------------------------------------------
  // Advanced patterns
  // -------------------------------------------------------------------------

  /**
   * Display a toast with an undo action button.
   * Useful for destructive actions that can be reverted.
   * @param message - Message to display
   * @param onUndo - Callback when undo button is clicked
   * @returns Toast ID for dismissal
   */
  toastWithUndo: (message: string, onUndo: () => void) => string | number;

  /**
   * Display a persistent toast that never auto-dismisses.
   * Must be manually dismissed using dismissToast(id).
   * @param message - Message to display
   * @param action - Optional action button configuration
   * @returns Toast ID for dismissal
   */
  toastPersistent: (message: string, action?: ToastAction) => string | number;

  /**
   * Display a loading toast that persists until dismissed.
   * Returns a dismiss function for convenience.
   * @param message - Loading message to display
   * @returns Function to dismiss the loading toast
   */
  toastLoading: (message: string) => () => void;

  /**
   * Display a promise-based toast that updates based on promise state.
   * Automatically handles loading, success, and error states.
   * @param promise - Promise to track
   * @param messages - Messages for each state
   * @returns The original promise (pass-through)
   */
  toastPromise: <T>(promise: Promise<T>, messages: PromiseMessages<T>) => Promise<T>;

  // -------------------------------------------------------------------------
  // Utilities
  // -------------------------------------------------------------------------

  /**
   * Dismiss a specific toast by ID.
   * @param id - Toast ID to dismiss (optional, dismisses last if not provided)
   */
  dismissToast: (id?: string | number) => void;

  /**
   * Dismiss all active toasts.
   */
  dismissAllToasts: () => void;
}

// =============================================================================
// Context
// =============================================================================

export const ToastContext = createContext<ToastContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Toast Provider Component
 *
 * Wraps the component tree and provides toast notification functions.
 * Should be placed high in the component tree, typically in App.tsx.
 *
 * @example
 * ```tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 * ```
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const value = useMemo<ToastContextValue>(
    () => ({
      // Simple notifications
      toastSuccess: (message, options) => toast.success(message, options),
      toastError: (message, options) => toast.error(message, options),
      toastWarning: (message, options) => toast.warning(message, options),
      toastInfo: (message, options) => toast.info(message, options),

      // Advanced patterns
      toastWithUndo: (message, onUndo) => toast.withUndo(message, onUndo),
      toastPersistent: (message, action) =>
        toast.persistent(message, action ? { action } : undefined),
      toastLoading: message => toast.loading(message),
      toastPromise: toast.promise,

      // Utilities
      dismissToast: id => toast.dismiss(id),
      dismissAllToasts: () => toast.dismissAll(),
    }),
    []
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};
